import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";
import RedeemCodeWidget from "../components/RedeemCodeWidget";
import { useAuth } from "../context/AuthContext";
import { API } from "../constants";

/**
 * /invite/<code> — landing page for shareable promo-link invites.
 * Shows the code's value + a one-click redeem (or sign-in / sign-up path).
 */
export default function InvitePage() {
  const { code } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!code) return;
    axios.get(`${API}/auth/invite/${encodeURIComponent(code)}`)
      .then((res) => setInfo(res.data))
      .catch(() => setInfo({ valid: false, code }))
      .finally(() => setLoading(false));
  }, [code]);

  return (
    <Layout>
      <div className="min-h-[calc(100vh-8rem)] flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-xl">
          {loading ? (
            <div className="card-glass p-10 text-center text-[#E8E4DC]" data-testid="invite-loading">Loading invite…</div>
          ) : !info?.valid ? (
            <div className="card-glass p-10 text-center border border-red-500/30" data-testid="invite-invalid">
              <h1 className="font-heading text-3xl font-bold text-white mb-3">Invite Unavailable</h1>
              <p className="text-[#E8E4DC] mb-6">The code <span className="font-mono text-red-400">{code}</span> is not valid.</p>
              <Link to="/pricing" className="btn-primary inline-block">See Pricing</Link>
            </div>
          ) : !info.available ? (
            <div className="card-glass p-10 text-center border border-yellow-500/30" data-testid="invite-full">
              <h1 className="font-heading text-3xl font-bold text-white mb-3">Invite Fully Claimed</h1>
              <p className="text-[#E8E4DC] mb-2">
                The code <span className="font-mono text-[#A3E635]">{info.code}</span> has reached its cap of {info.cap} redemptions.
              </p>
              <p className="text-[#E8E4DC]/70 text-sm mb-6">Lifetime access is still available at the regular price.</p>
              <Link to="/pricing" className="btn-primary inline-block">Get Lifetime — $39</Link>
            </div>
          ) : (
            <div className="card-glass p-10 border border-[#A3E635]/30" data-testid="invite-valid">
              <div className="text-center mb-6">
                <span className="inline-block text-xs font-mono px-3 py-1 rounded-full bg-[#A3E635]/15 text-[#A3E635] mb-3">
                  {info.is_personal ? `${info.referrer_name || "A backer"} invited you` : "You're invited"}
                </span>
                <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-3">
                  {info.pct >= 100 ? "Free Lifetime Access" : `${info.pct}% Off Lifetime`}
                </h1>
                <p className="text-[#E8E4DC]">
                  Redeem code <span className="font-mono text-[#A3E635] font-bold">{info.code}</span>
                  {info.pct >= 100 ? " and unlock Top Screen™ lifetime on the spot." : ` — pay just $${info.final_price.toFixed(2)} at checkout.`}
                </p>
                {info.cap != null && (
                  <p className="text-[#E8E4DC]/60 text-xs mt-2">
                    {info.used} of {info.cap} spots claimed
                  </p>
                )}
              </div>

              {user ? (
                <RedeemCodeWidget
                  initialCode={info.code}
                  onLifetimeGranted={() => navigate("/dashboard")}
                />
              ) : (
                <div className="text-center space-y-3">
                  <p className="text-[#E8E4DC] text-sm">Sign in or create a free account to redeem.</p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Link to={`/register?invite=${encodeURIComponent(info.code)}`} className="btn-primary" data-testid="invite-signup">
                      Create Account & Redeem
                    </Link>
                    <Link to={`/login?invite=${encodeURIComponent(info.code)}`} className="btn-secondary" data-testid="invite-login">
                      Sign In
                    </Link>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
